import { useState, useCallback, useRef, useEffect } from 'react';
import axios from 'axios';
import type { CancelTokenSource } from 'axios';
import type { Position, RouteData } from '../types';
import type { RouteProfile } from '../components/profileSelector/RouteProfileSelector';
import { calculateRoute } from '../utils/routeCalculation';
import { useToast } from '../contexts/ToastContext';
import { AnalyticsService } from '../services/analytics/HybridAnalyticsService';

export interface EnhancedRoutingState {
  isCalculating: boolean;
  progress: 'idle' | 'started' | 'processing' | 'finalizing';
  progressPercent: number; // Dynamic progress percentage
  timeElapsed: number;
  targetDuration: number; // Target duration for route calculation in milliseconds
  error: string | null;
  canCancel: boolean;
}

export interface UseEnhancedRoutingOptions {
  onComplete?: (routeData: RouteData | null) => void;
  onError?: (error: Error) => void;
  onCancel?: () => void;
  defaultTimeout?: number;
}

/**
 * Enhanced routing hook with timeout, cancellation, and progress tracking
 * Simpler approach - shows progress but blocks UI during calculation
 */
export const useEnhancedRouting = (options: UseEnhancedRoutingOptions = {}) => {
  const {
    onComplete,
    onError,
    onCancel,
    defaultTimeout = 30000 // 30 seconds default
  } = options;

  const { showWarning } = useToast();

  const [state, setState] = useState<EnhancedRoutingState>({
    isCalculating: false,
    progress: 'idle',
    progressPercent: 0,
    timeElapsed: 0,
    targetDuration: 15000, // Default 15 seconds
    error: null,
    canCancel: false
  });

  // Store last calculation parameters for retry
  const lastCalculationRef = useRef<{
    positions: Position[];
    profile: RouteProfile;
    timeout?: number;
  } | null>(null);

  const cancelTokenRef = useRef<CancelTokenSource | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Dynamic progress calculation based on route complexity and time
  const calculateDynamicProgress = useCallback((
    timeElapsed: number, 
    waypointCount: number, 
    currentStage: 'idle' | 'started' | 'processing' | 'finalizing'
  ): number => {
    // Base progress values for stages
    const stageProgress = {
      idle: 0,
      started: 15,
      processing: 30,
      finalizing: 90
    };

    // Route complexity factor (affects how fast progress increases)
    const complexityFactor = waypointCount <= 3 ? 1.5 : waypointCount <= 8 ? 1.0 : 0.7;
    
    // Time-based progress increment (slower for complex routes)
    const timeIncrement = Math.min(timeElapsed * 2 * complexityFactor, 60);
    
    // Combine stage progress + time-based increment
    const dynamicProgress = stageProgress[currentStage] + timeIncrement;
    
    // Cap progress at 95% until finalizing stage
    if (currentStage !== 'finalizing') {
      return Math.min(dynamicProgress, 85);
    }
    
    return Math.min(dynamicProgress, 98);
  }, []);

  // Timer functionality with dynamic progress
  const startTimer = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    
    timerRef.current = setInterval(() => {
      setState(prev => {
        const newTimeElapsed = prev.timeElapsed + 1;
        const waypointCount = lastCalculationRef.current?.positions?.length || 2;
        const newProgressPercent = calculateDynamicProgress(newTimeElapsed, waypointCount, prev.progress);
        
        return {
          ...prev,
          timeElapsed: newTimeElapsed,
          progressPercent: newProgressPercent
        };
      });
    }, 1000); // Update every 1000ms (1 second)
  }, [calculateDynamicProgress]);

  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // Reset state
  const resetState = useCallback(() => {
    setState({
      isCalculating: false,
      progress: 'idle',
      progressPercent: 0,
      timeElapsed: 0,
      targetDuration: 15000, // Default 15 seconds
      error: null,
      canCancel: false
    });
    stopTimer();
    
    // Don't cancel existing token during reset to avoid axios conflicts
    // Just clear the reference and let the old request complete or timeout naturally
    if (cancelTokenRef.current) {
      cancelTokenRef.current = null;
    }
  }, [stopTimer]);

  const determineTimeout = useCallback((waypointCount: number): number => {
    if (waypointCount <= 3) return Math.min(defaultTimeout, 25000); // 25s for simple routes (increased from 15s)
    if (waypointCount <= 8) return Math.min(defaultTimeout, 40000); // 40s for medium routes (increased from 25s)
    return defaultTimeout; // Full timeout for complex routes
  }, [defaultTimeout]);

  const calculateRouteWithEnhancement = useCallback(async (
    positions: Position[],
    profile: RouteProfile,
    customTimeout?: number,
    routeSessionId?: string | null, // Add route session ID parameter
    routeStorageId?: string | null  // Add route storage ID parameter
  ): Promise<RouteData | null> => {
    
    // Store parameters for potential retry
    const timeout = customTimeout || determineTimeout(positions.length);
    lastCalculationRef.current = { positions, profile, timeout: customTimeout };

    // Always create a completely fresh cancel token
    const originalCancelSource = axios.CancelToken.source();
    
    // Wrap the cancel function to detect who calls it
    const originalCancel = originalCancelSource.cancel;
    originalCancelSource.cancel = function(message) {
      return originalCancel.call(this, message);
    };
    
    cancelTokenRef.current = originalCancelSource;

    // Start calculation - clear any previous timer and error state
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    
    setState(prev => ({
      ...prev,
      isCalculating: true,
      progress: 'started',
      progressPercent: 15, // Initial progress for started stage
      canCancel: true,
      timeElapsed: 0,
      targetDuration: timeout, // Set the calculated target duration
      error: null // Clear any previous error to ensure spinner shows
    }));
    
    const calculationStartTime = Date.now(); // Track calculation timing
    startTimer();

    try {
      setState(prev => ({ 
        ...prev, 
        progress: 'processing', 
        progressPercent: Math.max(30, prev.progressPercent) // Ensure we're at least at 30%
      }));
      
      // Add a tiny delay to let any synchronous cancellations happen
      await new Promise(resolve => setTimeout(resolve, 10));
      
      const result = await calculateRoute(
        positions, 
        profile, 
        'geojson', 
        undefined, 
        undefined, 
        true, // bypassCache = true for retry to avoid cached cancelled promises
        {
          timeout,
          cancelToken: cancelTokenRef.current?.token
        }
      );
      
      // Check if calculation failed (returned null)
      if (result === null) {
        stopTimer();
        
        const errorMessage = 'Route calculation failed';
        setState(prev => ({
          ...prev,
          isCalculating: false,
          progress: 'idle',
          progressPercent: 0,
          error: errorMessage,
          canCancel: false
        }));
        
        const errorObj = new Error(errorMessage);
        onError?.(errorObj);
        return null;
      }
      
      // Only proceed if we haven't been cancelled
      if (cancelTokenRef.current) {
        
        // Track analytics for successful route calculation
        const calculationTimeMs = Date.now() - calculationStartTime;
        await AnalyticsService.trackRouteCalculated({
          routeSessionId: routeSessionId || null,
          routeStorageId: routeStorageId || null,
          routePointsCount: positions.length,
          calculationTimeMs,
          calculationStatus: 'success',
          bikeProfile: profile,
          routeDistanceM: result?.distance || 0, // Distance is already in meters
          elevationGainM: result?.ascend || 0
        });
        
        setState(prev => ({ 
          ...prev, 
          progress: 'finalizing', 
          progressPercent: Math.max(90, prev.progressPercent) // Jump to at least 90%
        }));
        onComplete?.(result);
        
        // Clean up after successful completion
        setTimeout(() => {
          resetState();
        }, 500); // Brief delay to show completion
        
        return result;
      } else {
        return null;
      }
      
    } catch (error: unknown) {
      console.error('❌ CATCH BLOCK: Route calculation error/cancellation occurred');
      console.error('❌ CATCH DEBUG: Error details:', {
        message: error instanceof Error ? error.message : 'Unknown error',
        isAxiosCancel: axios.isCancel(error),
        errorType: typeof error,
        errorName: error instanceof Error ? error.name : 'Unknown',
        tokenStillExists: !!cancelTokenRef.current,
        tokenCancelled: cancelTokenRef.current?.token?.reason !== undefined,
        tokenReason: cancelTokenRef.current?.token?.reason || 'no reason'
      });
      stopTimer();
      
      if (axios.isCancel(error)) {

        // Track analytics for cancelled route calculation
        const calculationTimeMs = Date.now() - calculationStartTime;
        await AnalyticsService.trackRouteCalculated({
          routeSessionId: routeSessionId || null,
          routeStorageId: routeStorageId || null,
          routePointsCount: positions.length,
          calculationTimeMs,
          calculationStatus: 'cancelled',
          bikeProfile: profile,
          routeDistanceM: 0,
          elevationGainM: 0
        });
        
        setState(prev => ({
          ...prev,
          isCalculating: false,
          progress: 'idle',
          progressPercent: 0,
          canCancel: false
        }));
        onCancel?.();
        return null;
      } else {
        console.error('💥 Route calculation failed:', error);
        const errorMessage = error instanceof Error ? error.message : 'Route calculation failed';
        
        // Track analytics for failed route calculation
        const calculationTimeMs = Date.now() - calculationStartTime;
        await AnalyticsService.trackRouteCalculated({
          routeSessionId: routeSessionId || null,
          routeStorageId: routeStorageId || null,
          routePointsCount: positions.length,
          calculationTimeMs,
          calculationStatus: 'error',
          bikeProfile: profile,
          routeDistanceM: 0,
          elevationGainM: 0
        });
        
        setState(prev => ({
          ...prev,
          isCalculating: false,
          progress: 'idle',
          progressPercent: 0,
          error: errorMessage,
          canCancel: false
        }));
        
        const errorObj = error instanceof Error ? error : new Error(errorMessage);
        onError?.(errorObj);
        return null;
      }
    }
  }, [defaultTimeout, determineTimeout, resetState, startTimer, stopTimer, onComplete, onCancel, onError]);

  const retryLastCalculation = useCallback(async () => {
    
    if (!lastCalculationRef.current) {
      console.warn('⚠️ STEP 1 FAILED: No previous calculation to retry');
      return;
    }
    
    const { positions, profile, timeout } = lastCalculationRef.current;

    
    // Call the calculation with the same parameters that were used before
    const result = await calculateRouteWithEnhancement(positions, profile, timeout);
    
    return result;
  }, [calculateRouteWithEnhancement, state]);

  const cancelCalculation = useCallback(() => {
    console.warn('🚫 CANCEL CALLED: cancelCalculation triggered');
    console.warn('🚫 CANCEL DEBUG: Current state:', {
      hasToken: !!cancelTokenRef.current,
      canCancel: state.canCancel,
      isCalculating: state.isCalculating,
      progress: state.progress
    });
    console.trace('🔍 CANCEL STACK: Who called cancelCalculation?');
    
    if (cancelTokenRef.current && state.canCancel) {
      console.warn('🚫 CANCELLING: Proceeding with cancellation...');
      cancelTokenRef.current.cancel('User cancelled request');
      
      // Show info toast about cancellation
      showWarning('Route calculation cancelled. Previous route restored.', {
        title: 'Calculation Cancelled',
        duration: 3000,
        position: 'bottom-center'
      });
      
      // Call the onCancel callback if provided
      onCancel?.();
      
      return true;
    }
    return false;
  }, [state.canCancel, onCancel, showWarning]);

  const clearError = useCallback(() => {
    setState(prev => ({ ...prev, error: null }));
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (cancelTokenRef.current) {
        cancelTokenRef.current.cancel('Component unmounting');
      }
    };
  }, []);

  return {
    isCalculating: state.isCalculating,
    progress: state.progress,
    progressPercent: state.progressPercent,
    timeElapsed: state.timeElapsed,
    targetDuration: state.targetDuration,
    error: state.error,
    canCancel: state.canCancel,
    calculateRoute: calculateRouteWithEnhancement,
    cancelCalculation,
    clearError,
    retryLastCalculation
  };
};